import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { DO_LOADING } from 'redux/actions/loading';
import { HIT_BOARD_STATUS } from 'redux/actions/board';
import { PayloadMoveBPType, PayloadWLASubmitType, ResponseMutationImportProcessType } from 'components/types/businessProcess';
import { toast } from 'react-toastify';
import { apiProcess, processService } from './process.service';


export const useProcessList = (boardId: null | string, enabled : boolean = true) => useQuery({
  queryKey: [apiProcess.treeViewList, boardId],
  queryFn: () => processService.getTreeViewList({
    board_id: boardId,
  }),
  enabled: !!boardId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});

export const useCVCList = (boardId: null | string, level: null | string, enabled : boolean = true) => useQuery({
  queryKey: [apiProcess.ValueChainList, boardId, level],
  queryFn: () => processService.getCVCList({
    board_id: boardId,
    level,
  }),
  enabled: !!boardId && !!level && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});

export const useWlaTemplateList = (processId: null | string, enabled : boolean = true) => useQuery({
  queryKey: [apiProcess.wlaTemplateList, processId],
  queryFn: async () => {
    try {
      return await processService.getWlaTemplateList({
        process_id: processId,
      });
    } catch (error) {
      if (error.raw.error && error.raw.status === 400) {
        return [];
      }

      throw error;
    }
  },
  enabled: !!processId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
});

export const useDetailprocess = (boardId: null | string, processId: null | string, enabled : boolean = true) => useQuery({
  queryKey: [apiProcess.detailProcess, boardId, processId],
  queryFn: () => processService.detailProcess({
    board_id: boardId,
    process_id: processId,
  }),
  enabled: !!boardId && !!processId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
});

export const useAPQCVersionList = (boardId: null | string, enabled : boolean = true) => useQuery({
  queryKey: [apiProcess.apqcVersionList_V2, boardId],
  queryFn: () => processService.getApqcVersion({ }),
  enabled: !!boardId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});

export const useAPQCLibraryList = (versionId: null | string, enabled : boolean = true) => useQuery({
  queryKey: [apiProcess.apqcLibraryList_V2, versionId],
  queryFn: () => processService.getApqcLibrary({
    apqc_version_id: versionId,
  }),
  enabled: !!versionId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});

export const useAPQCTemplateList = (libraryId: null | string, enabled : boolean = true) => useQuery({
  queryKey: [apiProcess.apqcTemplateList_V2, libraryId],
  queryFn: () => processService.getApqcTemplate({
    apqc_library_id: libraryId,
  }),
  enabled: !!libraryId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});

const useMutationCategoryProcess = <
TPayload, TResponse extends { message: string },
>
  (mutationFn: (payload:TPayload) => Promise<TResponse>) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (responseData: TResponse) => {
      const successMessage = responseData.message;

      void queryClient.invalidateQueries({
        queryKey: [apiProcess.treeViewList],
      });
      void queryClient.invalidateQueries({
        queryKey: [apiProcess.ValueChainList],
      });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'success',
          message: successMessage,
        },
      });
    },
    onError: (error) => {
      const errorMessage = error.message;
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'failed',
          message: errorMessage,
        },
      });
    },
  });
};

export const useCreateProcess = () => useMutationCategoryProcess(processService.createProcess);

export const useUpdateProcess = () => useMutationCategoryProcess(processService.updateProcess);

export const useCreateTreeProcess = () => useMutationCategoryProcess(processService.createTreeProcess);

export const useCreateChildProcess = () => useMutationCategoryProcess(processService.createChildProcess);

export const useWLATempalateSubmit = () => {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload:PayloadWLASubmitType) => processService.wlaTemplateSubmit(payload),
    onError: (error) => {
      const errorMessage = error.message;
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'failed',
          message: errorMessage,
        },
      });
    },
  });
};

const useMutationDeleteImportProcess = <
TPayload, TResponse extends { message: string },
>
  (mutationFn: (payload:TPayload) => Promise<TResponse>) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate: () => dispatch({ type: DO_LOADING, payload: true }),
    onSuccess: (responseData: TResponse) => {
      const successMessage = responseData.message;

      void queryClient.invalidateQueries({
        queryKey: [apiProcess.treeViewList],
      });
      void queryClient.invalidateQueries({
        queryKey: [apiProcess.ValueChainList],
      });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'success',
          message: successMessage,
        },
      });
    },
    onError: (error) => {
      const errorMessage = error.message;
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'failed',
          message: errorMessage,
        },
      });
    },
    onSettled: () => dispatch({ type: DO_LOADING, payload: false }),
  });
};

export const useDeleteProcess = () => useMutationDeleteImportProcess(processService.deleteProcess);

export const useImportBoardProcess = () => useMutationDeleteImportProcess(processService.importProcessBoard);

export const useCreateAPQC = () => useMutationDeleteImportProcess(processService.createAPQC);

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useMoveProcess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PayloadMoveBPType) => {
      const [result] = await Promise.all([processService.moveProcess(payload), delay(1000)]);

      return result;
    },
    onMutate: () => toast.loading('Loading.....', {
      containerId: 'business_process',
    }),
    onSuccess: (responseData, _variables, toastId) => {
      const successMessage = responseData.message;

      void queryClient.invalidateQueries({
        queryKey: [apiProcess.treeViewList],
      });
      void queryClient.invalidateQueries({
        queryKey: [apiProcess.CVCList],
      });

      toast.update(toastId, {
        render: successMessage,
        type: 'success',
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
        containerId: 'business_process',
      });
      console.log('dataMove', successMessage);
    },
    onError: (error, _variables, toastId) => {
      const errorMessage = error.message;

      toast.update(toastId, {
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
        containerId: 'business_process',
      });
    },
  });
};

export const useImportProcess = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processService.importProcess,
    onMutate: () => {
      dispatch({ type: DO_LOADING, payload: true });
    },
    onSuccess: (responseData) => {
      const successMessage = responseData.message;
      void queryClient.invalidateQueries({
        queryKey: [apiProcess.CVCList],
      });
      void queryClient.invalidateQueries({
        queryKey: [apiProcess.treeViewList],
      });

      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'success',
          message: successMessage,
        },
      });
    },
    onError: (error: ResponseMutationImportProcessType) => {
      const errorMessage = error.raw?.data || {};
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'failed',
          tempData: errorMessage,
          message: error.message,
          listView: Object.keys(errorMessage).length !== 0 ? 'listView' : '',
        },
      });
    },
    onSettled: () => {
      dispatch({ type: DO_LOADING, payload: false });
    },
  });
};
